import axios from "axios";
import serviceConfig from "../config/serviceConfig";

/**
 * Type definition for the upload file response.
 */
interface UploadFileResponse {
	message: string;
	success: boolean;
	data: string[]; // Array of file paths returned by the API
	errorMsg?: string;
}

/**
 * Uploads a file to the server.
 * @param file - The file to upload.
 * @param token - The access token for authentication.
 * @returns The filename of the uploaded file.
 */
export const uploadFile = async (
	file: File,
	token: string,
): Promise<string> => {
	try {
		const formData = new FormData();
		formData.append("files", file);

		const response = await axios.post<UploadFileResponse>(
			`${serviceConfig.api.baseUrl}${serviceConfig.routes.uploadImage}`,
			formData,
			{
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "multipart/form-data",
				},
			},
		);

		// Extract the first file path from the response data
		if (response.data.success && response.data.data.length > 0) {
			return response.data.data[0];
		} else {
			throw new Error("Failed to upload file: No file path returned");
		}
	} catch (error: any) {
		console.error(
			"Error uploading file:",
			error.response?.data || error.message,
		);
		throw new Error("Failed to upload file");
	}
};

/**
 * Retrieves the full URL for a previously uploaded file.
 * @param filename - The name of the file to fetch.
 * @returns The full URL to access the file.
 */
export const getFileUrl = async (filename: string): Promise<string> => {
	if (!filename) {
		throw new Error("Filename is required to generate the file URL");
	}
	try {
		const response = await axios.get<string>(
			`${serviceConfig.api.hostUrl}${filename}`,
		);

		return response.data; // Ensure this matches the API's response format
	} catch (error: any) {
		console.error(
			"Error retrieving file:",
			error.response?.data || error.message,
		);
		throw new Error("Failed to retrieve file");
	}
};
